import type { OnboardingEmailTemplate } from '../OnboardingEmailModal.types';
import { documentItem, optional, questionItem } from './templateItems';

const TRANSACTION_DETAILS = 'Transaction Details';
const PURCHASE_CONSIDERATION = 'Purchase Consideration';
const TRANSACTION_STRUCTURE_AND_TAX = 'Transaction Structure & Tax';
const COMPANY_DOCUMENTS = 'Company Documents/Information';
const DEVELOPED_TECHNOLOGY = 'Developed Technology';
const TRADE_NAME = 'Trade Name / Trademarks';
const CUSTOMER_RELATIONSHIPS = 'Customer Relationships';
const IPRD = 'In-Process R&D';
const INTERNAL_USE_SOFTWARE = 'Internal Use Software';
const NON_COMPETES = 'Non-Competes';
const LEASES = 'Favorable/Unfavorable Leases';
const ORDER_BACKLOG = 'Order Backlog';
const DEFERRED_REVENUE = 'Deferred Revenue';
const ASSEMBLED_WORKFORCE = 'Assembled Workforce';
const OTHER_INTANGIBLES = 'Other Intangible Assets';

// Based on the "ASC 805/IFRS 3 - Purchase Price Allocation Information Request List" workbook.
export const PPA_TEMPLATE: OnboardingEmailTemplate = {
  id: 'ppa-onboarding',
  label: 'PPA Onboarding (ASC 805 / IFRS 3)',
  emailSubject: 'Information request for your Purchase Price Allocation',
  valuationTypes: ['PPA (ASC)', 'PPA (IFRS 3)'],
  requestValuationType: 'ppa',
  introduction: `Hello {{CONTACT_FIRST_NAME}},

Thank you for partnering with us. We are ready to move forward with the Purchase Price Allocation analysis.

To help us build an accurate model and keep things moving efficiently, please reply to this email with your responses to the questions below and upload the requested documents to the portal. Where available, Excel versions are strongly preferred to ensure precision in our analysis.`,
  documents: [
    questionItem(
      TRANSACTION_DETAILS,
      'Closing date / Measurement date',
      'Closing Date of Transaction/Measurement Date'
    ),
    questionItem(TRANSACTION_DETAILS, 'Buyer', 'Buyer'),
    questionItem(TRANSACTION_DETAILS, 'Seller', 'Seller'),
    questionItem(TRANSACTION_DETAILS, 'Auditor', 'Auditor'),
    questionItem(TRANSACTION_DETAILS, 'Purpose (ASC 805 or IFRS 3)', 'Purpose', {
      description:
        'please indicate if the analysis is being performed for financial reporting purposes under ASC 805 or IFRS 3',
    }),
    questionItem(TRANSACTION_DETAILS, 'Transaction rationale', 'Transaction Rationale from Buyer\'s Perspective'),
    questionItem(TRANSACTION_DETAILS, 'Percent acquired', 'Percent of Target Company Acquired'),
    questionItem(
      TRANSACTION_DETAILS,
      'Reporting units / CGUs',
      'Number of reporting units/cash generating units ("CGUs")'
    ),
    questionItem(
      TRANSACTION_DETAILS,
      'Reporting unit allocation %',
      'If multiple reporting units are required, please provide the allocation %\'s of the purchase price to the reporting units',
      {
        description:
          'if available; if not, please advise if separate BEV analyses for each reporting unit are needed and we will provide a separate data request',
      }
    ),
    questionItem(TRANSACTION_DETAILS, 'Target marketing an exit?', 'Was the Target Company marketing an Exit?', {
      description: 'if so, did the Target Company receive more than one offer?',
    }),
    questionItem(TRANSACTION_DETAILS, 'Pivotal intangible asset(s)?', 'Pivotal Intangible Asset(s) acquired?'),
    questionItem(TRANSACTION_DETAILS, 'PCC election?', 'Private Company Council election made (Yes/No)?'),
    questionItem(TRANSACTION_DETAILS, 'Transfer pricing services?', 'Scalar also provides Transfer Pricing services', {
      description: 'please advise if this service is needed',
    }),

    questionItem(PURCHASE_CONSIDERATION, 'Cash', 'Purchase Consideration - Cash (Yes/No)'),
    questionItem(PURCHASE_CONSIDERATION, 'Equity/Stock', 'Purchase Consideration - Equity/Stock (Yes/No)', {
      description:
        'if yes, is a separate fair value analysis of stock required (we will provide a separate data request) and is the equity/stock contingent on future employment?',
    }),
    questionItem(
      PURCHASE_CONSIDERATION,
      'Earnout/Contingent consideration',
      'Purchase Consideration - Earnout/Contingent Consideration (Yes/No)',
      { description: 'if yes, is the earnout contingent on future employment?' }
    ),
    questionItem(PURCHASE_CONSIDERATION, 'Promissory note', 'Purchase Consideration - Promissory Note (Yes/No)'),

    questionItem(
      TRANSACTION_STRUCTURE_AND_TAX,
      'Structure of transaction',
      'Structure of Transaction: Non-taxable Stock Purchase, Taxable Asset Purchase (goodwill), or Asset Acquisition (no goodwill)',
      {
        description:
          'asset acquisition if the transaction did not meet the criteria to qualify as a business combination',
      }
    ),
    questionItem(
      TRANSACTION_STRUCTURE_AND_TAX,
      'NOL balance (stock deal)',
      'Existing federal net operating loss ("NOL") balance as of the Measurement Date',
      {
        description:
          'only applicable in a stock deal; for US reporting units, please identify the amounts related to pre- and post-2018 NOL balances',
      }
    ),
    questionItem(
      TRANSACTION_STRUCTURE_AND_TAX,
      'Projected tax amortization (stock deal)',
      'Projected tax amortization for each definite-lived intangible asset subject to amortization within the Reporting Unit',
      {
        description: 'only applicable in a stock deal; for each fiscal year until the end of the remaining useful life',
      }
    ),
    questionItem(TRANSACTION_STRUCTURE_AND_TAX, '338 election (stock deal)?', '338 Election made?', {
      description: 'only applicable in a stock deal',
    }),
    questionItem(TRANSACTION_STRUCTURE_AND_TAX, 'Country of incorporation', 'Target Company Country of Incorporation', {
      description: 'if international',
    }),
    questionItem(
      TRANSACTION_STRUCTURE_AND_TAX,
      'Revenue countries',
      'Which country(ies) does the Target Company source revenue?'
    ),
    questionItem(
      TRANSACTION_STRUCTURE_AND_TAX,
      'Tax location of intangibles & goodwill',
      'Where will intangible assets and goodwill live for tax purposes?',
      {
        description:
          'will the income from the developed technology and trade name be taxed at a consolidated global level or in specific countries? If specific countries, please list them with the approximate share of revenue attributable to each',
      }
    ),
    questionItem(
      TRANSACTION_STRUCTURE_AND_TAX,
      'R&D capitalized or expensed?',
      'Is R&D capitalized or expensed for tax purposes?',
      { description: 'if capitalized, please provide the amortization period (in years)' }
    ),

    documentItem(COMPANY_DOCUMENTS, 'Purchase agreements', 'Purchase Agreements and Closing Deal Documents'),
    documentItem(COMPANY_DOCUMENTS, 'Funds flow', 'Funds flow document', {
      description:
        'outlining the GAAP purchase consideration and wiring details of the consideration paid to the Seller; preferably in Excel',
    }),
    documentItem(
      COMPANY_DOCUMENTS,
      'Financials - Year end, last 3 years',
      'Historical annual income statements and fiscal year end ("FYE") balance sheets',
      { description: 'for three previous fiscal years if available' }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'LTM closing & opening balance sheet',
      'Last twelve months ("LTM") closing balance sheet and the opening balance sheet',
      { description: 'as of the Measurement Date' }
    ),
    questionItem(
      COMPANY_DOCUMENTS,
      'Book value as proxy for fair value?',
      'Confirm book value is a reasonable proxy for fair value for all assets and liabilities on the closing balance sheet',
      {
        description:
          'excluding items we are fair valuing; if not, please indicate the assets/liabilities that need to be fair valued separately',
      }
    ),
    documentItem(COMPANY_DOCUMENTS, '3-5 year projections', '3 - 5 year financial projections for the Target Company', {
      description:
        'revenues by product line and operating expenses by G&A, R&D, and S&M; reflecting the expectations of the Buyer as of the Measurement Date on a stand-alone, market participant basis without buyer-specific synergies',
    }),
    documentItem(
      COMPANY_DOCUMENTS,
      'Projected depreciation & capex',
      'Projected depreciation and capital expenditure requirements'
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Fixed assets tax life',
      'Tax Life on existing and new fixed assets for Target Company'
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Remaining service potential',
      'Remaining service potential on existing assets for Target Company'
    ),
    documentItem(COMPANY_DOCUMENTS, 'Public comparables', 'A list of public competitors and public comparable firms', {
      description: 'if available',
    }),
    documentItem(COMPANY_DOCUMENTS, 'Pitch deck', 'Latest Target Company pitch deck', { description: 'if available' }),

    questionItem(DEVELOPED_TECHNOLOGY, 'Description', 'Developed Technology - Description of asset', optional),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Multiple technologies?',
      'Are there multiple Developed Technologies with different levels of profitability and/or remaining economic useful lives?',
      { ...optional, description: 'if so, we will have some follow-up questions' }
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Annual obsolescence rate',
      'Developed Technology - Annual Obsolescence Rate',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Estimated useful life',
      'Developed Technology - Estimated Useful Life',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      '% R&D expansionary',
      '% of R&D Expense related to expansionary purposes (i.e., new products & new features)',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      '% R&D maintenance',
      '% of R&D Expense related to maintaining existing technology',
      optional
    ),
    questionItem(
      DEVELOPED_TECHNOLOGY,
      'Technology licensed?',
      'Has the technology been licensed? If so, at what rate?',
      optional
    ),

    questionItem(
      TRADE_NAME,
      'List of trade names',
      'List of Trade Names/Trademarks of Target Company that were acquired, including Domain Names',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Plan for name',
      'Plan for Name (i.e., sunset over a defined time period or continue to use moving forward)',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Time to transition',
      'Expected time to transition if a transition for the Trade Name is planned',
      optional
    ),
    questionItem(
      TRADE_NAME,
      'Trade name licensed?',
      'Has the Trade Name been licensed? If so, at what rate?',
      optional
    ),
    questionItem(TRADE_NAME, 'Brand recognition', 'Brand name recognition of Trade Name within the industry', optional),

    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'Relationship type',
      'Are the customer relationships contractual, recurring, transactional, etc.?',
      optional
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'PCC election questions',
      'If PCC election was made: can the asset be sold or licensed separate from the other assets of the business, and are customers under contract?',
      optional
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'Customer profitability/useful lives',
      'Are there customers with different levels of profitability and/or remaining economic useful lives?',
      { ...optional, description: 'if so, we will have some follow-up questions' }
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'Attrition history & expectations',
      'What has customer attrition been historically, and what are the expectations going forward?',
      optional
    ),
    documentItem(
      CUSTOMER_RELATIONSHIPS,
      'Historical attrition analysis',
      'Customer Relationships - Historical attrition analysis or revenue data by customer type',
      { ...optional, description: '3-5 years, to determine a historical attrition rate' }
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      '% revenue from existing customers',
      'What % of total revenue for the first and second fiscal years of the projections is attributable to Customer Relationships?',
      {
        ...optional,
        description:
          'what are the expectations going forward? Please also provide revenue growth attributable to each Customer Relationship over the forecast',
      }
    ),
    questionItem(
      CUSTOMER_RELATIONSHIPS,
      'S&M split',
      'What % of the S&M budget is attributable to maintaining existing Customer Relationships versus acquiring new Customers?',
      optional
    ),

    questionItem(
      IPRD,
      'Completion & monetization',
      'When is the IPR&D expected to be completed and monetized?',
      optional
    ),
    questionItem(
      IPRD,
      'Cost to recreate',
      'Expected total cost to reproduce the IPR&D from scratch to its state as of the acquisition date',
      { ...optional, description: 'from a market participant perspective' }
    ),
    questionItem(IPRD, 'Cost to complete', 'Total projected costs to complete the IPR&D', optional),
    questionItem(
      IPRD,
      'Obsolescence period',
      'Projected obsolescence period of the IPR&D once commercialization has been achieved',
      optional
    ),

    questionItem(
      INTERNAL_USE_SOFTWARE,
      'Sold to customers?',
      'Is the internal use software sold to customers/clients generating revenue?',
      optional
    ),
    questionItem(
      INTERNAL_USE_SOFTWARE,
      'Sold separately?',
      'Could the internal use software be sold separate to the existing technology offering?',
      optional
    ),
    documentItem(INTERNAL_USE_SOFTWARE, 'Internal Use Software template', 'Completed Internal Use Software template', {
      ...optional,
      description: 'estimated total investment and time period (in years) to recreate the software from scratch',
    }),

    questionItem(
      NON_COMPETES,
      'Covenantors & term',
      'With whom does the Non-Compete apply, and what is the term of the agreement?',
      optional
    ),
    questionItem(
      NON_COMPETES,
      'Probability to compete',
      'Probability that the covenantor competes if a non-compete was not in place',
      optional
    ),
    questionItem(
      NON_COMPETES,
      'Revenue impact',
      'Impact to revenue if the covenantor competes over the term of the agreement (as a % of revenue)',
      optional
    ),
    questionItem(
      NON_COMPETES,
      'Covenantor equity/earnout?',
      'Did any covenantors receive equity as part of the transaction or are eligible for contingent consideration?',
      optional
    ),

    documentItem(
      LEASES,
      'Off-market lease agreements',
      'Lease agreements not expected to be at market rates (i.e., favorable/unfavorable)',
      optional
    ),
    questionItem(LEASES, 'Contract details', 'Lease contract details', optional),
    questionItem(LEASES, 'Buyer economic rate/IBR', 'Current Economic Rate/IBR of Buyer', optional),

    questionItem(ORDER_BACKLOG, 'Backlog amount', 'Total order backlog acquired as of the Measurement Date', optional),
    questionItem(
      ORDER_BACKLOG,
      'Backlog fulfillment period',
      'Projected annual fulfillment period of backlog (i.e., waterfall)',
      optional
    ),
    questionItem(ORDER_BACKLOG, 'Probability of fulfillment', 'Probability of backlog fulfillment', optional),

    questionItem(
      DEFERRED_REVENUE,
      'Fair value separately?',
      'Does deferred revenue need to be fair valued separately?',
      {
        ...optional,
        description: 'if so, we will have some follow-up questions',
      }
    ),
    questionItem(
      DEFERRED_REVENUE,
      'Deferred revenue amount',
      'Total deferred revenue assumed as of the Measurement Date',
      optional
    ),
    questionItem(
      DEFERRED_REVENUE,
      'Deferred revenue fulfillment period',
      'Projected annual fulfillment period of deferred revenue (i.e., waterfall)',
      optional
    ),

    documentItem(ASSEMBLED_WORKFORCE, 'Assembled Workforce template', 'Completed Assembled Workforce template', {
      ...optional,
      description:
        'employees in place at the Target Company as of the Measurement Date, reflective of market participant assumptions',
    }),

    questionItem(
      OTHER_INTANGIBLES,
      'Other intangible assets',
      'Any other Intangible Assets not stated above that may require a separate fair value analysis?',
      { ...optional, description: 'please include a description' }
    ),
  ],
};
